import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, ArrowLeft, ExternalLink } from "lucide-react";

/**
 * ImagePreviewModal — full-screen image gallery with pinch/zoom + swipe.
 *
 * Features:
 *   - Pinch-to-zoom (touch) + Ctrl/Cmd+wheel zoom (desktop)
 *   - Double-tap to toggle 1× ↔ 2.5×
 *   - Swipe horizontally (when zoomed-out) to navigate the gallery
 *   - Keyboard: Esc closes, +/- zoom, 0 reset, ←/→ navigate
 *   - Click outside the image (or X button) to close
 *
 * Originally inlined in ChatsPage.jsx (~220 lines). Extracted Apr 2026 as
 * part of the refactor sprint to keep ChatsPage maintainable.
 */
const ImagePreviewModal = ({ images, initialIndex = 0, onClose }) => {
  const list = Array.isArray(images) && images.length ? images : [];
  const [index, setIndex] = useState(Math.max(0, Math.min(initialIndex, list.length - 1)));
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pinchRef = useRef({ dist: 0, startZoom: 1 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startOffset: { x: 0, y: 0 }, swipeOnly: false, didMove: false });
  const lastTapRef = useRef(0);

  const src = list[index];
  const canSwipe = list.length > 1;

  const goto = useCallback((i) => {
    const clamped = Math.max(0, Math.min(i, list.length - 1));
    setIndex(clamped);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [list.length]);

  const prev = useCallback(() => goto(index - 1), [index, goto]);
  const next = useCallback(() => goto(index + 1), [index, goto]);

  // Close on Escape; +/- for zoom; arrow keys for gallery navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(z * 1.25, 5));
      else if (e.key === "-") setZoom((z) => Math.max(z / 1.25, 1));
      else if (e.key === "0") { setZoom(1); setOffset({ x: 0, y: 0 }); }
      else if (e.key === "ArrowLeft" && canSwipe) prev();
      else if (e.key === "ArrowRight" && canSwipe) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, canSwipe, prev, next]);

  const dist2 = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: dist2(e.touches[0], e.touches[1]), startZoom: zoom };
    } else if (e.touches.length === 1) {
      // Double-tap zoom toggle
      const now = Date.now();
      if (now - lastTapRef.current < 260) {
        if (zoom > 1) { setZoom(1); setOffset({ x: 0, y: 0 }); }
        else { setZoom(2.5); }
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      dragRef.current = {
        dragging: true,
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startOffset: { ...offset },
        // When zoom is at 1 and we have a gallery, horizontal drag = swipe-to-next
        swipeOnly: zoom <= 1 && canSwipe,
        didMove: false,
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      const d = dist2(e.touches[0], e.touches[1]);
      const nextZoom = Math.max(1, Math.min(5, pinchRef.current.startZoom * (d / pinchRef.current.dist)));
      setZoom(nextZoom);
      if (nextZoom <= 1) setOffset({ x: 0, y: 0 });
      e.preventDefault();
    } else if (e.touches.length === 1 && dragRef.current.dragging) {
      const dx = e.touches[0].clientX - dragRef.current.startX;
      const dy = e.touches[0].clientY - dragRef.current.startY;
      dragRef.current.didMove = true;
      if (dragRef.current.swipeOnly) {
        // Show a soft horizontal offset while swiping so the user sees it's a swipe
        setOffset({ x: dx * 0.7, y: 0 });
      } else {
        setOffset({
          x: dragRef.current.startOffset.x + dx,
          y: dragRef.current.startOffset.y + dy,
        });
      }
    }
  };

  const onTouchEnd = (e) => {
    const d = dragRef.current;
    if (d.dragging && d.swipeOnly && d.didMove) {
      // Decide swipe direction — threshold 60px
      const finalDx = (e.changedTouches[0]?.clientX ?? d.startX) - d.startX;
      if (Math.abs(finalDx) > 60) {
        if (finalDx < 0) next(); else prev();
      } else {
        // Snap back
        setOffset({ x: 0, y: 0 });
      }
    }
    dragRef.current.dragging = false;
  };

  // Mouse wheel zoom (desktop)
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 30) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => Math.max(1, Math.min(5, z * factor)));
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startOffset: { ...offset }, swipeOnly: false, didMove: false };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setOffset({
      x: dragRef.current.startOffset.x + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startOffset.y + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  if (!src) return null;
  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center touch-none"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="image-preview-modal"
      onWheel={onWheel}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur rounded-full shadow-lg flex items-center justify-center hover:bg-white active:scale-95 z-10"
        data-testid="image-preview-close-btn"
        title="Close (Esc)"
      >
        <X size={18} />
      </button>

      {/* Desktop prev / next arrows — mobile uses swipe gesture */}
      {canSwipe && (
        <>
          <button
            onClick={prev}
            disabled={index === 0}
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 backdrop-blur rounded-full shadow-lg items-center justify-center hover:bg-white active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="preview-prev-btn"
            title="Previous (←)"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            onClick={next}
            disabled={index === list.length - 1}
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 backdrop-blur rounded-full shadow-lg items-center justify-center hover:bg-white active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="preview-next-btn"
            title="Next (→)"
          >
            <ArrowLeft size={18} className="rotate-180" />
          </button>
          {/* Counter + dots — top-left */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/90 backdrop-blur rounded-full shadow-lg px-3 py-1.5 z-10" data-testid="preview-counter">
            <span className="text-[11px] font-extrabold text-gray-700 tabular-nums">{index + 1} / {list.length}</span>
          </div>
        </>
      )}

      {/* Zoom HUD — bottom center */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full shadow-lg px-2.5 py-1.5 z-10">
        <button
          onClick={() => setZoom((z) => Math.max(1, z / 1.25))}
          className="w-8 h-8 rounded-full hover:bg-gray-100 active:scale-95 flex items-center justify-center text-lg font-bold"
          data-testid="preview-zoom-out-btn"
          title="Zoom out (-)"
        >−</button>
        <span className="min-w-[50px] text-center text-[11px] font-bold text-gray-700 tabular-nums" data-testid="preview-zoom-label">{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom((z) => Math.min(5, z * 1.25))}
          className="w-8 h-8 rounded-full hover:bg-gray-100 active:scale-95 flex items-center justify-center text-lg font-bold"
          data-testid="preview-zoom-in-btn"
          title="Zoom in (+)"
        >+</button>
        <button
          onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
          className="w-8 h-8 rounded-full hover:bg-gray-100 active:scale-95 flex items-center justify-center text-[10px] font-extrabold"
          data-testid="preview-zoom-reset-btn"
          title="Reset (0)"
        >1:1</button>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="ml-1 w-8 h-8 rounded-full hover:bg-gray-100 active:scale-95 flex items-center justify-center text-gray-700"
          title="Open in new tab"
          data-testid="preview-open-btn"
        >
          <ExternalLink size={13} />
        </a>
      </div>

      <img
        key={src}
        src={src}
        alt={`Preview ${index + 1} of ${list.length}`}
        draggable={false}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="max-w-[95vw] max-h-[85vh] rounded-xl shadow-2xl object-contain select-none transition-transform duration-75"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          cursor: zoom > 1 ? "grab" : canSwipe ? "grab" : "zoom-in",
          touchAction: "none",
        }}
      />
    </div>
  );
};

export default ImagePreviewModal;
